/**
 * Günlük yaşam: zaman çizelgesi ve rota planlama.
 *
 * İkisi de bilişsel becerinin gerçek hayata aktarımını hedefliyor —
 * saat tutmak, kapalı olanı hesaba katmak, aktarma yapmak. Klinikte
 * çocukların asıl zorlandığı yer burası ve piyasadaki programların
 * çoğunda bu modül hiç yok.
 *
 * ÜRETİM ÖNCE ÇÖZÜMÜ KURUYOR, sonra kısıtı ondan türetiyor. Tersi —
 * rastgele kısıt koyup çözüm aramak — iki şablonda da çalışmazdı:
 * çizelgede kısıtlar birbirini kilitliyor, rotada kapalı durak çoğu
 * tohumda zaten yolun dışında kalıyor. Aynı dersi aritmetik ağda ve
 * sayılı labirentte iki kez aldık.
 */

import { Rng } from '../../prng'
import { EXERCISE_VERSION, clampLevel, scale } from '../types'
import type { ExerciseTemplate, GradeResult, ItemResult } from '../types'

/* ------------------------------------------------------------------ *
 * Saat yardımcıları — dakika sayısı ile "14:30" arasında
 * ------------------------------------------------------------------ */

export function saatYaz(dakika: number): string {
  const s = Math.floor(dakika / 60)
  const d = dakika % 60
  return `${String(s).padStart(2, '0')}:${String(d).padStart(2, '0')}`
}

export function saatOku(metin: string): number | null {
  const m = String(metin).trim().match(/^(\d{1,2})[:.](\d{2})$/)
  if (!m) return null
  const s = Number(m[1])
  const d = Number(m[2])
  if (s > 23 || d > 59) return null
  return s * 60 + d
}

/* ================================================================== *
 * 34. Zaman çizelgesi
 *
 * Birkaç iş, süreleri, bir sabit randevu ve kapalı bir saat aralığı.
 * Çocuk hepsini çakışmadan yerleştiriyor.
 *
 * ÇÖZÜM ÖNCE KURULUYOR. Rastgele kısıt koyup "acaba çözülebilir mi"
 * diye bakmak, çözümsüz bir sayfa basma riski demekti — ve çözümsüz
 * çizelge, çocuğun kendi beceriksizliğine yoracağı türden bir hata.
 * Önce geçerli bir çizelge diziliyor, kapalı aralık o çizelgedeki bir
 * BOŞLUKTAN türetiliyor: yani kuralı sağlayan en az bir dizilim
 * olduğu yapı gereği biliniyor.
 *
 * Cevap TEK DEĞİL ve olmamalı: günü farklı sırayla kurmak da doğru.
 * Puanlama bu yüzden kanonik çizelgeyle karşılaştırmıyor, çocuğun
 * yazdığı çizelgeyi KURALLARA göre denetliyor.
 * ================================================================== */
export const schedulePlan: ExerciseTemplate = {
  code: 'schedule_plan',
  title: 'Günü planla',
  domains: ['working_memory_verbal', 'inhibition'],
  channels: ['digital', 'print'],
  params(level) {
    const l = clampLevel(level)
    return {
      /** Serbest iş sayısı (randevu hariç) */
      is: scale(l, 2, 5),
      /** Kapalı aralık kuralı 3. düzeyden sonra */
      kapali: l >= 3,
      /** Yerleştirme adımı (dakika): kabalaştıkça kolaylaşıyor */
      adim: l >= 6 ? 15 : 30,
    }
  },
  generate(seed, level) {
    const p = this.params(level)
    const rng = new Rng(seed)
    const isSayisi = p['is'] as number
    const adim = p['adim'] as number

    /**
     * Yer adı ayrı bir alan.
     *
     * Kapalı saat kuralı "Market 12:30 - 13:30 arası kapalı" diyor ve
     * bu adı ilk hali görünen addan regexle kırpıyordu
     * ("Markete uğra" → "Market"). "Kütüphaneye git" o regexten
     * "Kütüphaney" çıkıyordu: görünen metni ayrıştırmak, görünen metin
     * değiştiği an sessizce bozulan bir bağ.
     */
    const ISLER = [
      { ad: 'Bankaya git', yer: 'Banka', sure: 30 },
      { ad: 'Markete uğra', yer: 'Market', sure: 20 },
      { ad: 'Kütüphaneye git', yer: 'Kütüphane', sure: 45 },
      { ad: 'Kargoyu al', yer: 'Kargo şubesi', sure: 15 },
      { ad: 'Ekmek al', yer: 'Fırın', sure: 15 },
      { ad: 'Kuaföre git', yer: 'Kuaför', sure: 40 },
    ]

    const baslangic = 11 * 60
    const bitis = 17 * 60
    const randevu = { ad: 'Doktor randevusu', sure: 45, saat: 14 * 60 }

    const secilen = rng.shuffle(ISLER).slice(0, isSayisi)

    /**
     * Önce geçerli bir çizelge kuruluyor: işler randevunun önüne ve
     * arkasına, aralarında boşluk bırakarak diziliyor.
     */
    /**
     * Örnek çizelge PALET IZGARASINA oturuyor.
     *
     * Ekranda saat klavyeyle değil paletten seçiliyor ve palet
     * `adim` aralıklarla diziliyor. İlk hali süreleri doğrudan
     * topluyordu ve 15:15 gibi paletsiz saatler çıkıyordu: sayfanın
     * kendi örnek çözümü ekranda GİRİLEMEZ oluyordu.
     */
    const yukari = (t: number) => Math.ceil(t / adim) * adim
    const plan = new Map<string, number>()
    plan.set(randevu.ad, randevu.saat)
    const once = secilen.slice(0, Math.ceil(secilen.length / 2))
    const sonra = secilen.slice(once.length)

    let imlec = baslangic
    for (const is of once) {
      plan.set(is.ad, imlec)
      imlec = yukari(imlec + is.sure)
    }
    // Randevudan önce bitmeli; sığmadıysa boşluksuz sıkıştır
    if (imlec > randevu.saat) {
      imlec = baslangic
      for (const is of once) {
        plan.set(is.ad, imlec)
        imlec = yukari(imlec + is.sure)
      }
    }
    let imlec2 = yukari(randevu.saat + randevu.sure)
    for (const is of sonra) {
      plan.set(is.ad, imlec2)
      imlec2 = yukari(imlec2 + is.sure)
    }

    /**
     * Kapalı aralık, kurulan çizelgedeki bir BOŞLUKTAN türetiliyor.
     *
     * Böylece kural geçerli çizelgeyi bozmuyor ama rastgele bir
     * dizilimi bozuyor. Kuralın hangi işe uygulanacağı da o işin
     * çizelgedeki yerine bakılarak seçiliyor.
     */
    let kapaliKural: { is: string; yer: string; bas: number; son: number } | null = null
    if (p['kapali'] && secilen.length > 0) {
      const hedef = rng.pick(secilen)
      const basi = plan.get(hedef.ad)!
      // İşin bitiminden SONRAKİ bir aralık: örnek çizelge bozulmuyor
      const kapaliBas = yukari(basi + hedef.sure)
      const kapaliSon = Math.min(kapaliBas + 60, bitis)
      if (kapaliSon > kapaliBas) {
        kapaliKural = { is: hedef.ad, yer: hedef.yer, bas: kapaliBas, son: kapaliSon }
      }
    }

    const gorevler = [
      { ad: randevu.ad, sure: randevu.sure, sabit: randevu.saat },
      ...secilen.map((i) => ({ ad: i.ad, sure: i.sure, sabit: null as number | null })),
    ]

    const kurallar = [
      `Evden ${saatYaz(baslangic)} çıkıyorsun, ${saatYaz(bitis)} evde olmalısın.`,
      `${randevu.ad} ${saatYaz(randevu.saat)} ve kaçırılamaz.`,
      'İşler birbiriyle çakışmasın.',
      ...(kapaliKural
        ? [
            `${kapaliKural.yer} ${saatYaz(kapaliKural.bas)} - ${saatYaz(kapaliKural.son)} arası kapalı.`,
          ]
        : []),
      'Yol süresi hesaba katılmıyor.',
    ]

    /** Paletteki saatler: gün boyunca `adim` aralıklarla */
    const saatler: string[] = []
    for (let t = baslangic; t <= bitis - 15; t += adim) saatler.push(saatYaz(t))

    return {
      code: 'schedule_plan',
      version: EXERCISE_VERSION,
      level: clampLevel(level),
      seed: String(seed),
      params: p,
      domains: this.domains,
      prompt: 'Her işin kaçta başlayacağını yaz. Kuralların hiçbirini çiğneme.',
      spokenPrompt:
        'Günü planlayacaksın. Her işin kaçta başladığını yazacaksın; işler çakışmayacak ve randevuyu kaçırmayacaksın.',
      render: [
        { t: 'text', value: kurallar.map((k, i) => `${i + 1}. ${k}`).join('  '), size: 'md' },
        {
          t: 'rows',
          items: gorevler.map((g, i) => ({
            label: `${g.ad} (${g.sure} dk)${g.sabit !== null ? ` — ${saatYaz(g.sabit)}` : ''}`,
            blank: g.sabit === null,
            id: 'g' + i,
          })),
          columns: 1,
          palette: saatler.map((s) => ({ glyph: s })),
        },
      ],
      solution: {
        gorevler,
        baslangic,
        bitis,
        kapali: kapaliKural,
        ornek: [...plan.entries()].map(([ad, t]) => ({ ad, saat: t })),
      },
      grading: 'auto',
      estimatedSeconds: 60 + gorevler.length * 30,
      itemCount: gorevler.filter((g) => g.sabit === null).length,
    }
  },
  /**
   * Kanonik çizelgeyle DEĞİL, kurallarla karşılaştırılıyor.
   *
   * Günü başka bir sırayla kurmak da doğru; kanonikle karşılaştıran
   * bir puanlama doğru planı yanlış sayar ve uygulayıcı bunu
   * "planlayamadı" diye okur.
   */
  grade(instance, answer): GradeResult {
    const sol = instance.solution as {
      gorevler: { ad: string; sure: number; sabit: number | null }[]
      baslangic: number
      bitis: number
      kapali: { is: string; yer: string; bas: number; son: number } | null
    }
    const verilen = Array.isArray(answer) ? answer : []
    const yerlesim: { ad: string; bas: number; son: number; sure: number }[] = []
    const items: ItemResult[] = []

    sol.gorevler.forEach((g, i) => {
      const ham = g.sabit !== null ? saatYaz(g.sabit) : verilen[i]
      const t = g.sabit !== null ? g.sabit : saatOku(String(ham ?? ''))
      if (t !== null) yerlesim.push({ ad: g.ad, bas: t, son: t + g.sure, sure: g.sure })
      if (g.sabit !== null) return
      items.push({
        id: 'g' + i,
        beklenen: 'kurallara uyan bir saat',
        verilen: t === null ? null : saatYaz(t),
        dogru: false,
      })
    })

    /** Her iş için hangi kuralı çiğnediği ayrı ayrı. */
    let k = 0
    sol.gorevler.forEach((g, i) => {
      if (g.sabit !== null) return
      const kendi = yerlesim.find((y) => y.ad === g.ad)
      const it = items[k++]!
      if (!kendi) {
        it.beklenen = 'boş bırakılmamalı'
        return
      }
      const gunIcinde = kendi.bas >= sol.baslangic && kendi.son <= sol.bitis
      const cakisma = yerlesim.some(
        (y) => y.ad !== g.ad && kendi.bas < y.son && y.bas < kendi.son,
      )
      const kapaliIhlal =
        sol.kapali !== null &&
        sol.kapali.is === g.ad &&
        kendi.bas < sol.kapali.son &&
        sol.kapali.bas < kendi.son
      it.dogru = gunIcinde && !cakisma && !kapaliIhlal
      it.beklenen = !gunIcinde
        ? `${saatYaz(sol.baslangic)} - ${saatYaz(sol.bitis)} arasında`
        : cakisma
          ? 'başka bir işle çakışmayan bir saat'
          : kapaliIhlal
            ? `${saatYaz(sol.kapali!.bas)} - ${saatYaz(sol.kapali!.son)} dışında`
            : 'kurallara uyan bir saat'
      items[k - 1] = it
    })

    const dogru = items.filter((x) => x.dogru).length
    return {
      correct: dogru,
      total: items.length,
      accuracy: items.length ? dogru / items.length : 0,
      perItem: items.map((x) => x.dogru),
      items,
    }
  },
  anahtar(instance) {
    const sol = instance.solution as { ornek: { ad: string; saat: number }[] }
    return (
      'Bir çözüm: ' +
      sol.ornek.map((o) => `${o.ad} ${saatYaz(o.saat)}`).join('  ·  ') +
      '  (başka sıralamalar da doğru olabilir; kurallara bakın)'
    )
  },
}

/* ================================================================== *
 * 35. Rota planlama
 *
 * Birkaç hatlı bir ulaşım haritası, kapalı bir durak ve en az
 * duraklı rota.
 *
 * KAPALI DURAK, KAPALI OLMASAYDI GİDİLECEK YOLUN ÜSTÜNDE. Rastgele
 * bir durak kapatmak çoğu tohumda hiçbir şeyi değiştirmiyordu: çocuk
 * kuralı hiç okumadan doğru rotayı buluyor ve biz kısıtla çalıştığımızı
 * sanıyorduk. Üretim önce en kısa rotayı buluyor, kapalı durağı
 * ORADAN seçiyor ve kapalıyken rotanın gerçekten uzadığını
 * doğruluyor.
 *
 * Hatlar renkle DEĞİL çizgi biçimiyle ayrışıyor (§0.1): kâğıt
 * siyah-beyaz çıkıyor ve "kırmızı hatta aktarma yap" diyen bir
 * yönerge fotokopide anlamsız.
 * ================================================================== */
export const routePlan: ExerciseTemplate = {
  code: 'route_plan',
  title: 'Rota planla',
  domains: ['working_memory_visual', 'cognitive_flexibility'],
  channels: ['digital', 'print'],
  params(level) {
    const l = clampLevel(level)
    return {
      /** Üst ve alt hattaki durak sayısı */
      yatay: scale(l, 4, 7),
      /** Dikey bağlantılardaki ara durak sayısı */
      ara: l >= 5 ? 2 : 1,
    }
  },
  generate(seed, level) {
    const p = this.params(level)
    const rng = new Rng(seed)
    const yatay = p['yatay'] as number
    const ara = p['ara'] as number

    const ADLAR = [
      'Çarşı', 'Köprü', 'Park', 'Liman', 'Kale', 'Bahçe', 'Çeşme', 'Meydan',
      'Orman', 'Deniz', 'Tepe', 'Saray', 'Kule', 'Vadi', 'Yalı', 'Konak',
      'Bostan', 'Pınar', 'Sahil', 'Çınar',
    ]
    const adlar = rng.shuffle(ADLAR)
    let sayac = 0
    const yeniAd = () => adlar[sayac++]!

    type Durak = { ad: string; x: number; y: number }
    const duraklar = new Map<string, Durak>()
    const koy = (ad: string, x: number, y: number) => {
      duraklar.set(ad, { ad, x, y })
      return ad
    }

    // İki yatay hat
    const ust = Array.from({ length: yatay }, (_, i) =>
      koy(yeniAd(), 0.08 + (0.84 * i) / (yatay - 1), 0.18),
    )
    const alt = Array.from({ length: yatay }, (_, i) =>
      koy(yeniAd(), 0.08 + (0.84 * i) / (yatay - 1), 0.82),
    )

    /**
     * İKİ dikey bağlantı. Bir tane olsaydı, üstündeki bir durağı
     * kapatmak haritayı ikiye bölerdi ve sayfanın cevabı olmazdı.
     */
    const bagIndeksleri = rng.shuffle(Array.from({ length: yatay }, (_, i) => i))
    const b1 = Math.min(bagIndeksleri[0]!, bagIndeksleri[1]!)
    const b2 = Math.max(bagIndeksleri[0]!, bagIndeksleri[1]!)
    const dikeyKur = (i: number): string[] => {
      const x = 0.08 + (0.84 * i) / (yatay - 1)
      return Array.from({ length: ara }, (_, k) =>
        koy(yeniAd(), x, 0.18 + ((0.64 * (k + 1)) / (ara + 1))),
      )
    }
    const d1 = dikeyKur(b1)
    const d2 = dikeyKur(b2)

    const hatlar: { ad: string; duraklar: string[]; dash: string }[] = [
      { ad: 'Üst hat', duraklar: ust, dash: '' },
      { ad: 'Alt hat', duraklar: alt, dash: '10 6' },
      { ad: 'Bağlantı 1', duraklar: [ust[b1]!, ...d1, alt[b1]!], dash: '2 5' },
      { ad: 'Bağlantı 2', duraklar: [ust[b2]!, ...d2, alt[b2]!], dash: '12 4 2 4' },
    ]

    // Komşuluk çizgesi
    const komsu = new Map<string, Set<string>>()
    for (const d of duraklar.keys()) komsu.set(d, new Set())
    for (const h of hatlar) {
      for (let i = 1; i < h.duraklar.length; i++) {
        komsu.get(h.duraklar[i - 1]!)!.add(h.duraklar[i]!)
        komsu.get(h.duraklar[i]!)!.add(h.duraklar[i - 1]!)
      }
    }

    const rota = (a: string, b: string, yasak?: string): string[] | null => {
      const onceki = new Map<string, string | null>([[a, null]])
      const kuyruk = [a]
      while (kuyruk.length) {
        const x = kuyruk.shift()!
        if (x === b) break
        for (const y of komsu.get(x)!) {
          if (y === yasak || onceki.has(y)) continue
          onceki.set(y, x)
          kuyruk.push(y)
        }
      }
      if (!onceki.has(b)) return null
      const yol: string[] = []
      let x: string | null = b
      while (x) {
        yol.unshift(x)
        x = onceki.get(x)!
      }
      return yol
    }

    // Başlangıç ve hedef ayrı hatlarda: aktarma zorunlu olsun
    let kurgu: { bas: string; hedef: string; kapali: string; yol: string[] } | null = null
    for (let deneme = 0; deneme < 80 && !kurgu; deneme++) {
      const bas = rng.pick(ust)
      const hedef = rng.pick(alt)
      const serbest = rota(bas, hedef)
      if (!serbest || serbest.length < 4) continue
      const icerik = serbest.slice(1, -1)
      if (icerik.length === 0) continue
      const kapali = rng.pick(icerik)
      const kisitli = rota(bas, hedef, kapali)
      // Kural bağlayıcı mı: kapalıyken yol gerçekten uzuyor mu
      if (!kisitli || kisitli.length <= serbest.length) continue
      kurgu = { bas, hedef, kapali, yol: kisitli }
    }
    if (!kurgu) throw new Error('route_plan: kurgu bulunamadi')

    const renkler = ['#111', '#555', '#333', '#777']
    const items = hatlar.map((h, i) => ({
      points: h.duraklar.map((d) => [duraklar.get(d)!.x, duraklar.get(d)!.y] as [number, number]),
      id: 'h' + i,
      color: renkler[i]!,
      ...(h.dash ? { dash: h.dash } : {}),
    }))

    const etiketler = [...duraklar.values()].map((d) => ({
      x: d.x,
      y: d.y,
      text: d.ad === kurgu!.kapali ? `${d.ad} (KAPALI)` : d.ad,
      id: 'd' + d.ad,
    }))

    return {
      code: 'route_plan',
      version: EXERCISE_VERSION,
      level: clampLevel(level),
      seed: String(seed),
      params: p,
      domains: this.domains,
      prompt:
        `${kurgu.bas} durağından ${kurgu.hedef} durağına git. ${kurgu.kapali} durağı KAPALI, ` +
        `oradan geçemezsin. En az durakla giden yolu yaz.`,
      spokenPrompt:
        'Haritada bir durak kapalı. Oradan geçmeden, en az durağa uğrayarak hedefe gideceksin.',
      render: [
        { t: 'paths', items, labels: etiketler, height: 300 },
        {
          t: 'rows',
          items: [{ label: 'Rota', blank: true, id: 'rota', genis: true }],
          columns: 1,
          /** Durak adları paletten: haritadaki adı klavyede yazmak ayrı bir yük */
          palette: [...duraklar.keys()]
            .filter((d) => d !== kurgu!.kapali)
            .map((d) => ({ glyph: d })),
          paletteMode: 'sequence',
        },
      ],
      solution: {
        yol: kurgu.yol,
        bas: kurgu.bas,
        hedef: kurgu.hedef,
        kapali: kurgu.kapali,
        komsuluk: [...komsu.entries()].map(([a, b]) => [a, [...b]] as [string, string[]]),
      },
      grading: 'auto',
      estimatedSeconds: 60 + kurgu.yol.length * 10,
      itemCount: 2,
    }
  },
  /**
   * İki şey ayrı ayrı: geçerli rota mı, en kısası mı.
   *
   * Aynı uzunlukta başka rotalar olabiliyor; kanonik yolla
   * karşılaştırmak onları yanlış sayardı. Denetim komşuluk üzerinden.
   */
  grade(instance, answer): GradeResult {
    const sol = instance.solution as {
      yol: string[]
      bas: string
      hedef: string
      kapali: string
      komsuluk: [string, string[]][]
    }
    const komsu = new Map(sol.komsuluk.map(([a, b]) => [a, new Set(b)]))
    const ham = Array.isArray(answer) ? answer.join(' ') : String(answer ?? '')
    const yol = ham.split(/[\s,>→-]+/).map((x) => x.trim()).filter(Boolean)

    let gecerli = yol.length >= 2 && yol[0] === sol.bas && yol[yol.length - 1] === sol.hedef
    if (gecerli) {
      const gorulen = new Set<string>()
      for (let i = 0; i < yol.length && gecerli; i++) {
        const d = yol[i]!
        if (d === sol.kapali || gorulen.has(d) || !komsu.has(d)) gecerli = false
        gorulen.add(d)
        if (i > 0 && gecerli && !komsu.get(yol[i - 1]!)!.has(d)) gecerli = false
      }
    }
    const enKisa = gecerli && yol.length === sol.yol.length
    return {
      correct: (gecerli ? 1 : 0) + (enKisa ? 1 : 0),
      total: 2,
      accuracy: ((gecerli ? 1 : 0) + (enKisa ? 1 : 0)) / 2,
      perItem: [gecerli, enKisa],
      items: [
        {
          id: 'rota',
          beklenen: `${sol.bas} → ${sol.hedef}, ${sol.kapali} kapalı`,
          verilen: yol.length ? yol.join(' → ') : null,
          dogru: gecerli,
        },
        {
          id: 'enkisa',
          beklenen: `${sol.yol.length} durak`,
          verilen: gecerli ? `${yol.length} durak` : null,
          dogru: enKisa,
        },
      ],
    }
  },
  anahtar(instance) {
    const sol = instance.solution as { yol: string[]; kapali: string }
    return `${sol.kapali} kapalı. Bir çözüm (${sol.yol.length} durak): ${sol.yol.join(' → ')}`
  },
}
